// ─────────────────────────────────────────────────────────────
// Ephemeris Service, real planetary positions via astronomy-engine
// ─────────────────────────────────────────────────────────────

import * as Astronomy from 'astronomy-engine';

// ─── Planet definitions ──────────────────────────────────────

export const PLANETS = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars',
  'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto',
] as const;
export type Planet = (typeof PLANETS)[number];

export const PLANET_GLYPHS: Record<Planet, string> = {
  Sun: '☉', Moon: '☽', Mercury: '☿', Venus: '♀', Mars: '♂',
  Jupiter: '♃', Saturn: '♄', Uranus: '♅', Neptune: '♆', Pluto: '♇',
};

export const PLANET_NAMES_PT: Record<Planet, string> = {
  Sun: 'Sol', Moon: 'Lua', Mercury: 'Mercúrio', Venus: 'Vênus', Mars: 'Marte',
  Jupiter: 'Júpiter', Saturn: 'Saturno', Uranus: 'Urano', Neptune: 'Netuno', Pluto: 'Plutão',
};

/** Average daily speed in degrees, used for transit duration estimates. */
export const PLANET_MEAN_SPEED: Record<Planet, number> = {
  Sun: 0.9856, Moon: 13.176, Mercury: 1.383, Venus: 1.2, Mars: 0.524,
  Jupiter: 0.083, Saturn: 0.033, Uranus: 0.012, Neptune: 0.006, Pluto: 0.004,
};

/** Typical transit window in days (orb entry → orb exit). */
export const PLANET_TRANSIT_DURATION: Record<Planet, number> = {
  Sun: 2, Moon: 0.5, Mercury: 3, Venus: 4, Mars: 7,
  Jupiter: 30, Saturn: 60, Uranus: 180, Neptune: 365, Pluto: 730,
};

// ─── Zodiac definitions ─────────────────────────────────────

export const SIGNS = [
  'Áries', 'Touro', 'Gêmeos', 'Câncer', 'Leão', 'Virgem',
  'Libra', 'Escorpião', 'Sagitário', 'Capricórnio', 'Aquário', 'Peixes',
] as const;

export const SIGN_GLYPHS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];

export const SIGN_ELEMENTS: Record<string, string> = {
  'Áries': 'Fogo', 'Leão': 'Fogo', 'Sagitário': 'Fogo',
  'Touro': 'Terra', 'Virgem': 'Terra', 'Capricórnio': 'Terra',
  'Gêmeos': 'Ar', 'Libra': 'Ar', 'Aquário': 'Ar',
  'Câncer': 'Água', 'Escorpião': 'Água', 'Peixes': 'Água',
};

// ─── Helpers ─────────────────────────────────────────────────

function mod360(x: number): number {
  return ((x % 360) + 360) % 360;
}

export function longitudeToSign(longitude: number) {
  const lon = mod360(longitude);
  const signIndex = Math.floor(lon / 30);
  const degree = lon % 30;
  const degInt = Math.floor(degree);
  const minInt = Math.floor((degree - degInt) * 60);
  return {
    sign: SIGNS[signIndex],
    signIndex,
    glyph: SIGN_GLYPHS[signIndex],
    degree: parseFloat(degree.toFixed(4)),
    degreeFormatted: `${degInt}°${String(minInt).padStart(2, '0')}' ${SIGNS[signIndex]}`,
  };
}

export function longitudeToHouse(longitude: number, cusp0: number): number {
  const relative = mod360(longitude - cusp0);
  return Math.floor(relative / 30) + 1;
}

/** Returns the ecliptic longitude of the start (cusp 0°) of the Sun's sign, House 1 cusp. */
export function solarHouseCusp(sunLongitude: number): number {
  return Math.floor(mod360(sunLongitude) / 30) * 30;
}

// ─── Astronomy-engine body mapping ──────────────────────────

const BODY_MAP: Record<Planet, Astronomy.Body> = {
  Sun:     Astronomy.Body.Sun,
  Moon:    Astronomy.Body.Moon,
  Mercury: Astronomy.Body.Mercury,
  Venus:   Astronomy.Body.Venus,
  Mars:    Astronomy.Body.Mars,
  Jupiter: Astronomy.Body.Jupiter,
  Saturn:  Astronomy.Body.Saturn,
  Uranus:  Astronomy.Body.Uranus,
  Neptune: Astronomy.Body.Neptune,
  Pluto:   Astronomy.Body.Pluto,
};

/** Get ecliptic longitude for a body at a given date. */
export function getEclipticLongitude(planet: Planet, date: Date): number {
  try {
    const body = BODY_MAP[planet];
    if (planet === 'Sun') {
      // Sun: use GeoVector for ecliptic longitude
      const vec = Astronomy.GeoVector(body, date, true);
      const ecl = Astronomy.Ecliptic(vec);
      return mod360(ecl.elon);
    }
    const vec = Astronomy.GeoVector(body, date, true);
    const ecl = Astronomy.Ecliptic(vec);
    return mod360(ecl.elon);
  } catch {
    return 0;
  }
}

/** Compute daily speed in degrees/day (negative = retrograde). */
export function getDailySpeed(planet: Planet, date: Date): number {
  try {
    const prev = new Date(date.getTime() - 86_400_000);
    const next = new Date(date.getTime() + 86_400_000);
    const l1 = getEclipticLongitude(planet, prev);
    const l2 = getEclipticLongitude(planet, next);
    let diff = l2 - l1;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    return diff / 2; // average over 2 days for stability
  } catch {
    return PLANET_MEAN_SPEED[planet];
  }
}

// ─── Planet position interface ──────────────────────────────

export interface PlanetPosition {
  planet: Planet;
  longitude: number;
  latitude: number;
  sign: string;
  signGlyph: string;
  degree: number;
  degreeFormatted: string;
  house: number;
  isRetrograde: boolean;
  speed: number; // degrees/day, negative = retrograde
}

/** Calculate positions of all 10 planets for a given date.
 *  Houses use the Solar House system: House 1 = Sun's sign, House 2 = next sign, etc.
 */
export function calculatePlanetPositions(date: Date): PlanetPosition[] {
  // Determine solar house cusp (start of Sun's sign = cusp of House 1)
  const sunLon = getEclipticLongitude('Sun', date);
  const cusp0  = solarHouseCusp(sunLon);

  return PLANETS.map((planet) => {
    const longitude = getEclipticLongitude(planet, date);
    const speed = getDailySpeed(planet, date);

    let latitude = 0;
    try {
      if (planet !== 'Sun') {
        const vec = Astronomy.GeoVector(BODY_MAP[planet], date, true);
        const ecl = Astronomy.Ecliptic(vec);
        latitude = ecl.elat;
      }
    } catch { /* keep 0 */ }

    const { sign, glyph, degree, degreeFormatted } = longitudeToSign(longitude);

    return {
      planet,
      longitude,
      latitude,
      sign,
      signGlyph: glyph,
      degree,
      degreeFormatted,
      house: longitudeToHouse(longitude, cusp0),
      isRetrograde: speed < 0,
      speed,
    };
  });
}

// ─── ASC / MC calculation ───────────────────────────────────

export function calculateAscMC(
  date: Date,
  lat: number,
  lng: number,
): { asc: number; mc: number } {
  const gast = Astronomy.SiderealTime(date); // GAST in hours
  const ramc = mod360((gast + lng / 15) * 15); // RAMC in degrees

  const eps = 23.4393 * (Math.PI / 180); // obliquity
  const phi = lat * (Math.PI / 180);
  const ramcRad = ramc * (Math.PI / 180);

  // MC
  let mc = Math.atan2(Math.sin(ramcRad), Math.cos(ramcRad) * Math.cos(eps)) * (180 / Math.PI);
  mc = mod360(mc);

  // ASC
  const asc = Math.atan2(
    -Math.cos(ramcRad),
    Math.sin(ramcRad) * Math.cos(eps) + Math.tan(phi) * Math.sin(eps),
  ) * (180 / Math.PI);

  return { asc: mod360(asc), mc };
}

// ─── Moon phase ─────────────────────────────────────────────

export interface MoonPhaseInfo {
  phase: number; // 0–1 (0=new, 0.5=full)
  illumination: number; // 0–100%
  name: string;
  namePt: string;
}

export function getMoonPhase(date: Date): MoonPhaseInfo {
  const sunLon = getEclipticLongitude('Sun', date);
  const moonLon = getEclipticLongitude('Moon', date);
  let elongation = mod360(moonLon - sunLon);

  const phase = elongation / 360; // 0–1
  const illumination = Math.round((1 - Math.cos(elongation * Math.PI / 180)) / 2 * 100);

  let name: string;
  let namePt: string;
  if (elongation < 22.5) { name = 'New Moon'; namePt = 'Lua Nova'; }
  else if (elongation < 67.5) { name = 'Waxing Crescent'; namePt = 'Lua Crescente'; }
  else if (elongation < 112.5) { name = 'First Quarter'; namePt = 'Quarto Crescente'; }
  else if (elongation < 157.5) { name = 'Waxing Gibbous'; namePt = 'Gibosa Crescente'; }
  else if (elongation < 202.5) { name = 'Full Moon'; namePt = 'Lua Cheia'; }
  else if (elongation < 247.5) { name = 'Waning Gibbous'; namePt = 'Gibosa Minguante'; }
  else if (elongation < 292.5) { name = 'Last Quarter'; namePt = 'Quarto Minguante'; }
  else if (elongation < 337.5) { name = 'Waning Crescent'; namePt = 'Lua Minguante'; }
  else { name = 'New Moon'; namePt = 'Lua Nova'; }

  return { phase, illumination, name, namePt };
}
